import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { langMeta, detectAndApplyClientLang, changeLanguageSafe } from "@/i18n";
import { useIsEnglishRoute } from "@/components/LocalizedLink";
import { isEnglishPath, toEnglishPath } from "@/lib/route-i18n";
import { getStoredLang, setStoredLang } from "@/lib/lang-preference";
import { supabase } from "@/integrations/supabase/client";

/**
 * Runs client-side after hydration: detects preferred language from
 * localStorage/navigator and syncs <html lang>+dir. Initial SSR render
 * always uses 'sv' to avoid hydration mismatches.
 */
export function LangSync() {
  const { i18n } = useTranslation();
  const english = useIsEnglishRoute();
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Apply detected language once after mount. På /en-sidorna styr URL:en
  // språket – då ska inget lagrat val skriva över engelskan.
  useEffect(() => {
    if (english) return;
    detectAndApplyClientLang();
  }, [english]);

  // Språkvalet följer kontot: har användaren sparat ett språk på sitt konto
  // används det på nya enheter där inget lokalt val finns.
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      const pref = data.session?.user.user_metadata?.["preferred_lang"];
      if (typeof pref !== "string" || !pref) return;
      if (getStoredLang()) return;
      setStoredLang(pref);
      if (pref !== "en") void changeLanguageSafe(pref);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Har besökaren tidigare valt engelska skickas hen till motsvarande
  // /en-adress när en engelsk version av sidan finns.
  useEffect(() => {
    if (isEnglishPath(pathname)) return;
    if (getStoredLang() !== "en") return;
    const target = toEnglishPath(pathname);
    if (target === pathname) return;
    void router.navigate({ to: target, search: (prev: unknown) => prev as never, replace: true });
  }, [pathname, router]);

  // Keep <html lang>+dir in sync whenever language changes.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const meta = langMeta(english ? "en" : i18n.language);
    document.documentElement.lang = meta.code;
    document.documentElement.dir = meta.dir;
  }, [i18n.language, english]);

  return null;
}
